import { RefObject } from 'react';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import IconButton from '../IconButton/IconButton';
import { ExpandedState } from './ResizableTextArea';

interface ExpandCollapseButtonProps {
  // The expanded/collapsed state of the input (EXPANDED, COLLAPSED, UNSET)
  expandedState: ExpandedState;
  // Callback fired when the Expand/Collapse icon is clicked in COLLAPSED state
  onExpand: () => void;
  // Callback fired when the Expand/Collapse icon is clicked in EXPANDED state
  onCollapse: () => void;
  // A ref pointing to the expandable input
  textAreaRef: RefObject<HTMLTextAreaElement>;
  // CSS value to set minimum collapsible height
  minHeight: string;
}

/**
 * This is a component for use in the ResizableTextArea component. It renders an expand/collapse button that
 * allows users to expand and collapse the input. To keep the input from expanding forever, set a `max-height`
 * on the element.
 */
export const ExpandCollapseButton = ({
  expandedState,
  onExpand,
  onCollapse,
  textAreaRef,
  minHeight,
}: ExpandCollapseButtonProps) => {
  const ExpandButton = () => (
    <IconButton className="ExpandCollapseButton" icon={<UnfoldMoreIcon />} onClick={onExpand} />
  );
  const CollapseButton = () => (
    <IconButton className="ExpandCollapseButton" icon={<UnfoldLessIcon />} onClick={onCollapse} />
  );

  switch (expandedState) {
    case ExpandedState.EXPANDED:
      return <CollapseButton />;
    case ExpandedState.COLLAPSED:
      return <ExpandButton />;
    case ExpandedState.UNSET:
    default:
      if (textAreaRef.current) {
        // If the user has not expanded or collapsed the input yet, we start with no button
        // then switch to 'collapse' when the input has started increasing
        const textAreaHeight = parseInt(getComputedStyle(textAreaRef.current).height);
        const minTextAreaHeight = parseInt(minHeight);
        if (textAreaHeight > minTextAreaHeight) {
          return <CollapseButton />;
        }
      }
      return null;
  }
};
